import { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SortableTable from '../components/SortableTable';
import { fetchFeatureFlags, fetchOverviewStats } from '../services/firestore';

function initials(value = '') {
  return String(value || 'WB')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'WB';
}

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [flags, setFlags] = useState({});

  useEffect(() => {
    (async () => {
      const [overview, featureFlags] = await Promise.all([fetchOverviewStats(), fetchFeatureFlags()]);
      setStats(overview);
      setFlags(featureFlags);
    })();
  }, []);

  const signupColumns = useMemo(() => [
    {
      key: 'name',
      header: 'Business',
      accessor: (biz) => biz.businessName || biz.id,
      render: (biz) => (
        <div className="wb-merchant-cell">
          <span className="wb-avatar">{initials(biz.businessName || biz.id)}</span>
          <span>
            <span className="wb-cell-main">{biz.businessName || biz.id}</span>
            <span className="wb-cell-sub">{biz.city || biz.location || biz.id}</span>
          </span>
        </div>
      ),
    },
    {
      key: 'plan',
      header: 'Plan',
      accessor: (biz) => biz.plan || 'free',
      render: (biz) => <span className={`wb-tag wb-tag--${String(biz.plan || 'free').toLowerCase()}`}>{biz.plan || 'free'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (biz) => biz.planStatus || '-',
      render: (biz) => <span className={`wb-tag wb-tag--${String(biz.planStatus || 'unknown').toLowerCase()}`}>{biz.planStatus || '-'}</span>,
    },
    {
      key: 'created',
      header: 'Created',
      accessor: 'createdAt',
      render: (biz) => (biz.createdAt?.toDate ? biz.createdAt.toDate().toLocaleDateString() : '-'),
    },
  ], []);

  if (!stats) return <div className="wb-skeleton wb-skeleton--large" />;

  const planRows = Object.entries(stats.planDistribution)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);

  return (
    <>
      {flags.maintenanceMode && <div className="wb-card" style={{ borderColor: '#c62828', color: '#c62828' }}>maintenanceMode is active.</div>}
      <div className="wb-dashboard-hero">
        <div>
          <div className="wb-page-kicker">Last 30 days</div>
          <h2>Good afternoon, WorkBill team.</h2>
          <p>{stats.totalMerchants.toLocaleString('en-IN')} merchants across India. Keep an eye on trials, failed tasks, and webhook health.</p>
        </div>
        <div className="wb-actions-line">
          <Link className="wb-button-link" to="/operations/broadcasts">New broadcast</Link>
        </div>
      </div>

      <div className="wb-grid">
        <div className="wb-card wb-stat-card"><span>Total merchants</span><strong>{stats.totalMerchants.toLocaleString('en-IN')}</strong><small>Live accounts</small></div>
        <div className="wb-card wb-stat-card"><span>Active paid</span><strong>{stats.activePaid.toLocaleString('en-IN')}</strong><small>Subscription active</small></div>
        <div className="wb-card wb-stat-card"><span>Active trials</span><strong>{stats.activeTrials.toLocaleString('en-IN')}</strong><small>Needs conversion focus</small></div>
        <div className="wb-card wb-stat-card"><span>Estimated MRR</span><strong>Rs {stats.estimatedMRR.toLocaleString('en-IN')}</strong><small>Current plan pricing</small></div>
      </div>

      <div className="wb-split-grid">
        <div className="wb-card">
          <div className="wb-card-head">
            <div>
              <div className="wb-page-kicker">Today</div>
              <h3 className="wb-section-title">Recent Signups</h3>
            </div>
            <span className="wb-muted">Last {stats.recentSignups.length} merchants</span>
          </div>
          <SortableTable
            columns={signupColumns}
            rows={stats.recentSignups}
            emptyText="No recent signups found."
            defaultSort={{ key: 'created', dir: 'desc' }}
          />
        </div>

        <div className="wb-card">
          <div className="wb-card-head">
            <div>
              <div className="wb-page-kicker">Active accounts</div>
              <h3 className="wb-section-title">Plan Distribution</h3>
            </div>
          </div>
          <div className="wb-list-panel">
            {planRows.map(([key, count]) => {
              const pct = stats.totalMerchants ? Math.round((count / stats.totalMerchants) * 100) : 0;
              return (
                <div key={key}>
                  <div className="wb-list-row">
                    <span>{key}</span>
                    <strong>{count.toLocaleString('en-IN')} <span className="wb-muted">/{pct}%</span></strong>
                  </div>
                  <div className="wb-meter"><span style={{ width: `${Math.max(4, pct)}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
